import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img src={LOGO_URL} alt="Berkshire Africa" className="h-10" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-lg text-slate-600 mb-8">
            Last updated: March 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              Berkshire Africa Inc. ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our investment platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We may collect the following types of information:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, company name, and other contact details you provide through our inquiry forms or registration process.</li>
              <li><strong>Investment Information:</strong> Information related to your investment interests, accreditation status, and investment history (for LP partners).</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, and usage data collected through cookies and similar technologies.</li>
              <li><strong>Communication Data:</strong> Records of correspondence when you contact us.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Respond to your inquiries and provide requested information about investment opportunities</li>
              <li>Process and manage LP investments and partnerships</li>
              <li>Send relevant updates about our portfolio companies and investment performance</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve our website and services</li>
              <li>Protect against fraud and unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">4. Information Sharing</h2>
            <p className="text-slate-600 mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Service providers who assist in our operations (under strict confidentiality agreements)</li>
              <li>Legal and regulatory authorities when required by law</li>
              <li>Professional advisors including lawyers, accountants, and auditors</li>
              <li>Portfolio companies (only with your consent for co-investment opportunities)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, and access controls.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">6. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data (subject to legal requirements)</li>
              <li>Object to processing of your data</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. For LP investors, we retain records for the duration of the investment relationship plus the period required by applicable regulations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">8. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this privacy policy or wish to exercise your rights, please contact us at:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-900 font-semibold">Berkshire Africa Inc.</p>
              <p className="text-slate-600">Email: invest@berkshireafrica.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2025 Berkshire Africa Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
